import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Ticket, Search, Printer, LayoutDashboard, Calendar, Clock, Users, Building, Bot, LogOut, User, Package, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { printDepositTicket } from "@/utils/printTicket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTicketSearch = () => {
  const navigate = useNavigate();
  const [ticketNumber, setTicketNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [deposit, setDeposit] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const getAuth = useCallback(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  const handleSearch = async () => {
    if (!ticketNumber.trim()) {
      toast.error("Veuillez entrer un numéro de ticket");
      return;
    }
    const headers = getAuth();
    if (!headers) return;
    setLoading(true);
    setNotFound(false);
    setDeposit(null);
    try {
      const response = await axios.get(`${API}/admin/deposit-slip/search?ticket_number=${encodeURIComponent(ticketNumber.trim())}`, { headers });
      setDeposit(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Erreur lors de la recherche");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="admin-sidebar hidden md:block">
        <div className="flex items-center justify-center mb-10">
          <img src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" alt="Pressing Sakura" className="h-16 w-auto bg-white rounded-lg p-1"/>
        </div>
        <nav className="space-y-2">
          <div className="admin-nav-item" onClick={() => navigate("/admin/dashboard")}><LayoutDashboard className="w-5 h-5"/><span>Tableau de bord</span></div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/planning")}><Calendar className="w-5 h-5"/><span>Planning</span></div>
          <div className="admin-nav-item active"><Ticket className="w-5 h-5"/><span>Ticket</span></div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/slots")}><Clock className="w-5 h-5"/><span>Gérer les créneaux</span></div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/customers")}><Users className="w-5 h-5"/><span>Clients</span></div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/customers?filter=pro")}><Building className="w-5 h-5"/><span>Clients Pro</span></div>
          <div className="admin-nav-item" onClick={() => navigate("/admin/sakura")}><Bot className="w-5 h-5"/><span>IA Sakura</span></div>
        </nav>
        <div className="absolute bottom-6 left-6">
          <button onClick={handleLogout} className="admin-nav-item text-slate-400 hover:text-white"><LogOut className="w-5 h-5"/><span>Déconnexion</span></button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-auto pb-20 md:pb-10">
        <div className="md:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold flex items-center gap-2"><Ticket className="w-6 h-6 text-pink-500"/>Recherche Ticket</h1>
            <Button variant="ghost" size="icon" onClick={handleLogout}><LogOut className="w-5 h-5"/></Button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/dashboard")} className="flex-shrink-0"><LayoutDashboard className="w-4 h-4 mr-1"/>Dashboard</Button>
            <Button size="sm" className="bg-pink-500 text-white flex-shrink-0"><Ticket className="w-4 h-4 mr-1"/>Ticket</Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/customers")} className="flex-shrink-0"><Users className="w-4 h-4 mr-1"/>Clients</Button>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}><Ticket className="w-8 h-8 text-pink-500"/>Recherche Ticket</h1>
        </div>

        <Card className="max-w-xl mx-auto mb-8">
          <CardHeader><CardTitle className="text-center text-lg">Entrez le numéro du ticket</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"/>
                <Input type="text" placeholder="Ex: 5502" value={ticketNumber} onChange={(e) => setTicketNumber(e.target.value)} onKeyPress={handleKeyPress} className="pl-10 text-lg h-12" autoFocus/>
              </div>
              <Button onClick={handleSearch} disabled={loading} className="h-12 px-6 bg-pink-500 hover:bg-pink-600">
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"/> : <><Search className="w-5 h-5 mr-2"/>Rechercher</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {notFound && (
          <Card className="max-w-xl mx-auto border-red-200 bg-red-50">
            <CardContent className="p-6 text-center"><p className="text-red-600 font-medium">Aucun ticket trouvé avec le numéro "{ticketNumber}"</p></CardContent>
          </Card>
        )}

        {deposit && (
          <Card className="max-w-xl mx-auto border-green-200 bg-green-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Badge className="bg-pink-500 text-lg px-3 py-1">N° {deposit.ticket_number}</Badge></CardTitle>
                <Button onClick={() => printDepositTicket(deposit)} className="bg-green-600 hover:bg-green-700"><Printer className="w-5 h-5 mr-2"/>Imprimer</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <User className="w-5 h-5 text-pink-500"/>
                <div>
                  <p className="font-bold">{deposit.customer_name}</p>
                  <p className="text-sm text-slate-500">{new Date(deposit.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-3"><Package className="w-5 h-5 text-pink-500"/><span className="font-bold">Articles déposés</span></div>
                <div className="space-y-2">
                  {deposit.items?.map((item, index) => (<div key={index} className="flex justify-between text-sm"><span>{item.name}</span><span className="font-bold">x{item.quantity}</span></div>))}
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between font-bold"><span>Total articles</span><span>{deposit.items?.reduce((sum, i) => sum + i.quantity, 0)}</span></div>
              </div>
              {deposit.total_amount && (<div className="p-3 bg-pink-100 rounded-lg border border-pink-200 text-center"><span className="text-lg font-bold text-pink-700">Montant : {deposit.total_amount} €</span></div>)}
              {deposit.remarks && (<div className="p-3 bg-amber-50 rounded-lg border border-amber-200"><p className="text-sm font-medium text-amber-800"><strong>Remarques :</strong> {deposit.remarks}</p></div>)}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminTicketSearch;
